import { useState } from "react";
import { useNavigate } from 'react-router-dom';
import CustomList from '../../../core/components/core/Util/List/CustomList/CustomList';
import CustomListContainer from '../../../core/components/core/Util/List/CustomList/CustomListContainer';
import { loadData } from '../../../core/components/core/Util/List/CustomList/loadData';
import config from '../../config/config';
import { chooseProfileFilter } from '../../helpers/chooseProfileFilter';
import { expenseStatusCell } from '../../helpers/expenseStatusCell';
import { createExpenseActionsCell } from '../../helpers/expenseActionsCell';
import { useDispatch } from 'react-redux';
import PayExpense from './Modals/PayExpense';
import CancelExpense from './Modals/CancelExpense';
import Fetch from '../../../core/util/core/Fetch';

function List() {
    const profileFilter = chooseProfileFilter();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [showPayModal, setShowPayModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);

    const getInitialOrganizationType = () => {
        return profileFilter.organization_type || 'default';
    };

    const [organizationType, setOrganizationType] = useState(getInitialOrganizationType);
    const [filterValues, setFilterValues] = useState({});

    const openPayModal = (expense) => {
        setSelectedExpense(expense);
        setShowPayModal(true);
    };

    const openCancelModal = (expense) => {
        setSelectedExpense(expense);
        setShowCancelModal(true);
    };

    const handleCloseModal = () => {
        setSelectedExpense(null);
        setShowPayModal(false);
        setShowCancelModal(false);
        dispatch({ type: 'UPDATE_SESSION', key: 'custom_list_reload', value: new Date() });
    };

    const pathFrontEnd = '/gci/expenses/';
    const endpointBackend = config.endpoints.backend;

    const paths = {
        pathAdd: pathFrontEnd + 'add',
        pathEdit: pathFrontEnd + 'edit',
        pathView: pathFrontEnd + 'view',
        endpointDelete: `${endpointBackend}gci/expenses/delete`,
        endpoint: `${endpointBackend}gci/expenses/getall`,
        endpointGetAllCompanies: `${endpointBackend}gci/companies/getall`,
        endpointGetAllVentures: `${endpointBackend}gci/ventures/getall`,
        endpointGetAllProjects: `${endpointBackend}gci/projects/getall`,
    };

    const isDefaultOrganizationType = organizationType === 'default';

    const filters = {
        validateOnChange: true,
        showGoBackButton: false,
        showSubmitButton: false,
        handleSubmit: loadData,
        fields: [
            !profileFilter.gci_company_id ? {
                name: 'gci_company_id',
                type: 'select',
                label: 'Empresa',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllCompanies,
                    label: 'name',
                    value: 'id'
                },
                validations: { required: false },
                handleChange: async (element, field, values) => {
                    const companyId = values?.gci_company_id;
                    setFilterValues(values);
                    if (!companyId) {
                        setOrganizationType('default');
                        return;
                    }
                    const company = await Fetch.post(`${config.endpoints.backend}gci/companies/getById`, { id: companyId });
                    setOrganizationType(company?.organization_type || 'default');
                },
                columnSize: 4
            } : {},
            isDefaultOrganizationType ? {
                name: 'gci_venture_id',
                type: 'select',
                label: 'Empreendimento',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllVentures,
                    label: 'enterprise_name',
                    value: 'id',
                    params: { filter: { ...profileFilter } }
                },
                validations: { required: false },
                columnSize: 4
            } : {
                name: 'gci_project_id',
                type: 'select',
                label: 'Projeto',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllProjects,
                    label: 'project_name',
                    value: 'id',
                    params: { filter: { ...profileFilter } }
                },
                validations: { required: false },
                columnSize: 4
            },
            {
                name: 'status',
                type: 'select',
                label: 'Status',
                options: [
                    { value: 'paid', label: 'Paga' },
                    { value: 'pending', label: 'Pendente' },
                    { value: 'overdue', label: 'Atrasada' },
                    { value: 'partially_paid', label: 'Parcialmente Paga' },
                    { value: 'cancelled', label: 'Cancelada' },
                ],
                validations: { required: false },
                columnSize: 4
            },
            {
                name: 'emission_date',
                name_start: 'emission_date_start',
                name_end: 'emission_date_end',
                type: 'date_period',
                label: 'Data de Emissão',
                validations: { required: false },
                columnSize: 4,
            },
            {
                name: 'filtro',
                type: 'submit',
                label: 'Pesquisar',
                columnSize: 1
            },
            {
                name: 'reset',
                type: 'reset',
                label: 'Limpar',
                columnSize: 1
            },
        ]
    };
    filters.values = filterValues;

    let list = {
        labelTitle: 'Lista de Despesas',
        labelTitleButtonAdd: 'Incluir nova despesa',
        fieldsShowOnDelete: ['value'],
        canDelete: false,
        formFilters: filters,
        filter: {
            ...profileFilter
        },
        columns: [
            {
                name: 'venture_or_project',
                label: 'Empreendimento/Projeto',
                width: '18%'
            },
            {
                name: 'expense_type',
                label: 'Tipo de Despesa'
            },
            {
                name: 'accounting_plan',
                label: 'Plano de Contas',
                width: '18%'
            },
            {
                name: 'supplier',
                label: 'Fornecedor',
            },
            {
                name: 'value',
                label: 'Valor da Despesa',
            },
            {
                name: 'total_paid',
                label: 'Valor Pago',
            },
            {
                name: 'payment_method',
                label: 'Forma de Pagamento',
            },
            {
                name: 'due_date',
                label: 'Data de Vencimento',
            },
            {
                name: 'document_type',
                label: 'Tipo do Doc',
            },
            {
                name: 'document_number',
                label: 'Nº Doc',
            },
            {
                name: 'description',
                label: 'Descrição',
                width: '18%'
            },
            {
                name: 'formattedStatus',
                label: 'Status',
                width: '14%',
                cell: expenseStatusCell
            },
            {
                name: 'actions',
                label: 'Ações',
                width: '10%',
                cell: createExpenseActionsCell(openPayModal, openCancelModal)
            }
        ],
        columnsButtons: []
    };

    list = { ...list, ...paths };

    return (
        <div>
            <CustomListContainer
                pathAdd={list.pathAdd}
                labelTitle={list.labelTitle}
                labelTitleButtonAdd={list.labelTitleButtonAdd}
                buttons={[{
                    name: 'recurring',
                    label: 'Despesa recorrente',
                    class: 'btn btn-info',
                    icon: 'fa-solid fa-repeat',
                    callback: () => navigate(pathFrontEnd + 'recurring'),
                }]}
                content={<CustomList key={organizationType} list={{ ...list, ...paths }} />}
            />

            {/* --------------------------
                MODAL DE PAGAMENTO
            --------------------------- */}
            {showPayModal && (
                <PayExpense
                    show={showPayModal}
                    onClose={handleCloseModal}
                    expenseId={selectedExpense?.id}
                />
            )}

            {/* --------------------------
                MODAL DE CANCELAMENTO
            --------------------------- */}
            {showCancelModal && (
                <CancelExpense
                    show={showCancelModal}
                    onClose={handleCloseModal}
                    expenseId={selectedExpense?.id}
                />
            )}
        </div>
    );
}

export default List;
